import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import LanguageProvider from "@/contexts/LanguageProvider";
import { CalculatorFlowProvider } from "@/contexts/CalculatorFlowContext";

const Index = lazy(() => import("./pages/Index"));
const CalculatorEntry = lazy(() => import("./pages/CalculatorEntry"));
const CalculatorMeasurementsWithDimensions = lazy(() => import("./pages/CalculatorMeasurementsWithDimensions"));
const CalculatorMeasurementsNoDimensions = lazy(() => import("./pages/CalculatorMeasurementsNoDimensions"));
const CalculatorCustomization = lazy(() => import("./pages/CalculatorCustomization"));
const CalculatorSummary = lazy(() => import("./pages/CalculatorSummary"));
const CalculatorDetails = lazy(() => import("./pages/CalculatorDetails"));
const CalculatorEstimate = lazy(() => import("./pages/CalculatorEstimate"));
const AdminPricing = lazy(() => import("./pages/AdminPricing"));
const Preview = lazy(() => import("./pages/Preview"));
const QuotePrint = lazy(() => import("./pages/QuotePrint"));
const MyCalculations = lazy(() => import("./pages/MyCalculations"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LanguageProvider>
        <CalculatorFlowProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/calculator/measurements" element={<CalculatorEntry />} />
                  <Route path="/calculator/measurements/with-dimensions" element={<CalculatorMeasurementsWithDimensions />} />
                  <Route path="/calculator/measurements/no-dimensions" element={<CalculatorMeasurementsNoDimensions />} />
                  <Route path="/calculator/customization" element={<CalculatorCustomization />} />
                  <Route path="/calculator/summary" element={<CalculatorSummary />} />
                  <Route path="/calculator/details" element={<CalculatorDetails />} />
                  <Route path="/calculator/estimate" element={<CalculatorEstimate />} />
                  <Route path="/admin/pricing" element={<AdminPricing />} />
                  <Route path="/preview" element={<Preview />} />
                  <Route path="/quote/print" element={<QuotePrint />} />
                  <Route path="/my-calculations" element={<MyCalculations />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </CalculatorFlowProvider>
      </LanguageProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
